// Considered: three siblings (TypePropertyRowMolecule / TypeRelationshipRowMolecule /
// TypeArcRowMolecule) as scoped by the spec — rejected because all three want the same
// (label, secondary, badges, provenance, trailing) shape, so one domain-neutral row makes
// provenance render identically everywhere it is carried. Considered: FormRowAtom for the
// row rhythm — rejected because it is a bare flex container (children + align/justify/gap/
// wrap) with no slots of its own, so composing it would mean nesting a flex container inside
// a flex container for the same result, while this row's five ordered slots each need their
// own wrapping behaviour that a single shared `wrap` cannot express.
// See spec Design System Reuse Audit.
import React, { useId } from 'react';
import { useTranslation } from 'localization';
import TagAtom, { type TagTone } from '../../atoms/Tag/TagAtom';
import InputAtom from '../../atoms/Input/InputAtom';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import ChipsAtom from '../../atoms/Chips/ChipsAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import './TypeFieldRowMolecule.scss';

export interface TypeDefinitionRowBadge {
  label: string;
  tone?: TagTone;
}

export interface TypeDefinitionRowProvenance {
  isInherited: boolean;
  /**
   * The ancestor's resolved DISPLAY NAME (B7) — never the raw `schema_id` the wire carries.
   * The caller resolves this through its lineage walk before it reaches this row; omitted
   * (unresolvable/unviewable ancestor) renders the unknown-ancestor label below.
   */
  inheritedFromSchema?: string | null;
  /**
   * Declared directly on the type before every property was an aspect's (`trail-composer.jsx:1414`,
   * `p.legacy`) — read-only, and distinct from ancestor inheritance: the row is the type's OWN, just
   * predates the aspect model. Takes precedence over `isInherited` when both are somehow set, since
   * the prototype's own guard is `p.legacy && !p.inherited && !p.fromAspect`.
   */
  isLegacy?: boolean;
  /** The contract datum the rendered tag carries (`data-datum`) — omitted renders no datum. */
  datum?: string;
}

export interface TypeDefinitionRowSelectOption {
  label: string;
  value: string;
}

/**
 * The real control a field is edited through. Renamed from `TypeDefinitionRowMolecule`
 * (EAINT-895 Phase 3) once it grew a genuine `'edit'` mode: Phase 1/2 rendered every
 * variant `disabled` with no write path to forget to gate; Phase 3 sections wire
 * `mode="edit"` plus `onChange`/`onCommit` per control, while everything not yet
 * migrated keeps the old `'disabled'` (visible, inert) treatment as the default.
 *
 * Derived, never-editable values (counts, versions, the "brings" summary, the whole
 * Advanced shelf) deliberately keep static `secondary`/`trailing` text — rendering
 * them as disabled inputs would imply an authoring capability that is not merely
 * switched off but absent.
 */
export type TypeDefinitionRowControlSpec =
  | { kind: 'text'; value: string; placeholder?: string; onChange?: (value: string) => void }
  | { kind: 'multiline'; value: string; placeholder?: string; onChange?: (value: string) => void }
  /**
   * A whole-number or decimal field (`type="number"`, `inputmode` from `step` — ux-craft F2).
   * The draft is held locally while the field has focus and parsed ON BLUR (F3): a
   * per-keystroke parse coerces a half-typed value (an emptied field mid-edit) to a number
   * the author never typed. `onChange` receives `null` for an emptied field so the caller
   * decides what empty means; a draft that does not parse, or falls outside `min`/`max`,
   * reverts to the committed value on blur.
   */
  | { kind: 'number'; value: number | null; min?: number; max?: number; step?: number; placeholder?: string; onChange?: (value: number | null) => void }
  | { kind: 'select'; value: string; options?: TypeDefinitionRowSelectOption[]; onChange?: (value: string) => void }
  | { kind: 'switch'; checked: boolean; onChange?: (checked: boolean) => void }
  | { kind: 'chips'; values: string[]; placeholder?: string; onChange?: (values: string[]) => void }
  /**
   * A control the caller composes itself, rendered in the field's control slot. The row
   * owns the field's SHAPE (label above, revert beside it, hint below); a domain control
   * that is none of the five above — a segmented pair, a set of togglable kind chips —
   * still belongs inside that shape rather than beside it. The caller labels its own
   * control, so `ariaLabel` names the field for the walker, not the node.
   */
  | { kind: 'custom'; node: React.ReactNode };

/**
 * A control plus the accessible name it is announced by. `ariaLabel` is required
 * rather than optional: a list row (a property, a relationship) has no visible
 * field label to borrow, so a control without one would be an unnamed input.
 */
export type TypeDefinitionRowControl = TypeDefinitionRowControlSpec & {
  ariaLabel: string;
  /** Marks the control that should absorb spare width in a multi-control row. */
  grow?: boolean;
  /**
   * Fires when an edit-mode control's value should be persisted — on blur for
   * text-like controls, immediately after `onChange` for switch/select/chips
   * (there is no blur event that means anything for a toggle). Never called
   * outside `mode="edit"`.
   */
  onCommit?: () => void;
  /** The contract datum this control renders (`data-datum` on the control element). */
  datum?: string;
  /** The contract KIND of that datum, when it differs from the control's own (`data-kind`). */
  datumKind?: string;
};

/**
 * `'edit'` — the real, interactive control, wired to the caller's `onChange`/`onCommit`.
 * `'locked'` — static text plus a lock chip (a permission lock, e.g. `canEdit: false`,
 * or a structural one, e.g. `is_abstract` — the chip does not distinguish which).
 * `'disabled'` — the real control, rendered switched off. This is Phase 1/2's universal
 * preview treatment, kept as the default for any section a given build phase has not yet
 * wired to editing — spec `readOnly field — Renders as: static text plus a lock glyph chip`
 * only applies to `'locked'`; an unwired-but-unlocked field stays visibly a form control.
 * `'ghost'` — an agent-proposed value awaiting review (EAINT-895 Phase 5): the real,
 * interactive control (hand-editing a ghost keeps the edit — `editingAGhostByHandKeepsIt`),
 * on the violet ghost surface, carrying the `proposed` mark, the agent's rationale, and
 * Keep/Discard actions instead of whatever `trailing` the caller passed.
 */
export type TypeFieldRowMode = 'edit' | 'locked' | 'disabled' | 'ghost';

/**
 * Why a locked field is locked. The chip says the reason, because "locked" alone
 * makes a reader hunt for a permission they may already have. `platform` is the
 * prototype's `platform-named` chip and applies to a platform-owned type's fields;
 * the others are the reasons an ORG-owned type still refuses an edit — which is
 * why one chip could not serve them all (an org type showing "platform-named" on
 * its own system name was simply false).
 */
export type TypeFieldLockReason = 'platform' | 'immutable' | 'structural' | 'derived' | 'sealed';

export interface TypeFieldRowMoleculeProps {
  /** The field name for a form-shaped row. Omit for list rows, whose identity is their first control. */
  label?: string;
  secondary?: string;
  /** Rendered before the controls — the glyph beside the name it marks, say. */
  leading?: React.ReactNode;
  /**
   * Rendered immediately after `label`, inside the row's name cluster — a
   * qualifier that belongs TO the name rather than beside the row (the aspect
   * row's "was <system name>" when the attachment renames it).
   */
  afterLabel?: React.ReactNode;
  /**
   * Row-level facts rendered after `secondary` and before the controls — the
   * provenance/version/needs chips a list row carries. Distinct from `badges`,
   * which is a flat tone-tagged list; `meta` is caller-composed so each chip can
   * carry its own contract datum.
   */
  meta?: React.ReactNode;
  /** Which reason the lock chip states. Defaults to `platform`. */
  lockReason?: TypeFieldLockReason;
  /**
   * The row's real controls. One entry for a form-shaped row (Identity,
   * Placement); several for a list row that edits a record inline (a
   * property's name + type + required).
   */
  controls?: TypeDefinitionRowControl[];
  badges?: TypeDefinitionRowBadge[];
  /** Renders identically wherever provenance is carried (properties, perspectives, relationships, aspects). */
  provenance?: TypeDefinitionRowProvenance;
  trailing?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  /**
   * The contract's container path for THIS row when it sits inside a real collection
   * (`each(rows) > list-item` — the repetition segments PAR-5 requires; see
   * `scripts/lib/container-path.js`). Omitted for callers that don't place the row in a
   * decomposed list — most don't, since a row's own datum is usually flat scenery.
   */
  'data-container'?: string;
  /** @see TypeFieldRowMode. Defaults to `'disabled'` — the pre-Phase-3 universal treatment. */
  mode?: TypeFieldRowMode;
  /** `mode="ghost"` only: what the agent read/inferred, shown beside the `proposed` mark. */
  ghostRationale?: string;
  /** `mode="ghost"` only: applies this row's current (possibly hand-edited) value. */
  onKeepGhost?: () => void;
  /** `mode="ghost"` only: discards this row without applying it. */
  onDiscardGhost?: () => void;
  /** `mode="ghost"` only: Escape anywhere in this row returns focus to the agent thread's input
   * (spec Keyboard map: "Agent rail … Escape from a ghost returns to the thread"). */
  onGhostEscape?: () => void;
  /**
   * The prototype datum this row renders (e.g. `ident.name`), from the
   * decomposition contract. Emitted as `data-datum` so the parity walker can
   * find the row in a capture and diff its kind, layout, container, order and
   * state against the prototype's tree — a check that reads the rendered DOM,
   * never the source.
   */
  datum?: string;
  /**
   * Overrides the auto-derived `data-kind`. Needed whenever the row's
   * contract-declared kind is not simply "the first control's kind" — e.g. a
   * `field`/`icon` kind row that has no interactive control at all (Advanced's
   * read-only shelf), or a control kind the vocabulary map below does not cover.
   * Otherwise `data-kind` is derived from `controls[0].kind`.
   */
  kind?: string;
  /**
   * `stacked` puts the label above the control (Identity/Placement's form
   * fields); `inline` (the default) keeps today's label-beside-control rhythm
   * (Advanced's toggle list, list rows). Contract-binding — see
   * `stackedFieldPutsLabelAboveControl`.
   */
  layout?: 'stacked' | 'inline';
  /** Contract datum for the visible label leaf, when the contract addresses it separately from the control (e.g. `adv.attachments#2`). */
  labelDatum?: string;
  /** Contract datum for `secondary`, when the contract addresses it as its own leaf (a `note` kind, typically). */
  secondaryDatum?: string;
  /** A hint/explanation leaf rendered below the row — the contract's `hint` kind. */
  hint?: React.ReactNode;
  /** Contract datum for `hint`. */
  hintDatum?: string;
  /**
   * A blur-validation message (ux-craft F1: appears once the field is left, clears as soon
   * as the value is valid). Renders below the row and marks the first control `aria-invalid`,
   * described by this text via `aria-describedby`. The caller owns when it is populated —
   * this row has no validation rules of its own.
   */
  errorMessage?: React.ReactNode;
  /** Contract datum for `errorMessage`, when the contract addresses it as its own leaf. */
  errorDatum?: string;
  /**
   * Where `hint` sits. `below` (the default) gives it its own line under the row.
   * `inline` keeps it on the row's one line — `title │ hint │ control at the far right`,
   * the policy-row shape Advanced's list uses (`type-composer.jsx:1736-1757`, `.t-row`) —
   * and lets the label take its natural width rather than the controls column.
   */
  hintPlacement?: 'below' | 'inline';
  /**
   * Whether the name cluster may wrap. `wrap` (the default) lets label, secondary
   * and meta flow onto further lines as the row narrows. `nowrap` holds the row to
   * ONE line down to the tablet cut — the prototype's tight list row
   * (`studio.css:526`, `.t-rows-tight .t-row-asp { flex-wrap: nowrap }`) — with
   * the caller's `afterLabel` / `meta` content declaring what gives (truncates)
   * when the width runs out. Below `bp.$medium` the row wraps regardless, so it
   * still reflows at 320px (ux-craft L1).
   */
  wrap?: 'wrap' | 'nowrap';
}

/**
 * Maps this row's internal control-kind vocabulary to the contract's kind
 * vocabulary — `text`/`multiline`/`switch` are implementation names; the
 * contract says `input`/`textarea`/`toggle`. Left unmapped, every text/
 * multiline/switch-backed row fails PAR-4 by kind mismatch alone.
 */
const CONTROL_KIND_TO_DATUM_KIND: Record<TypeDefinitionRowControlSpec['kind'], string> = {
  text: 'input',
  multiline: 'textarea',
  number: 'input',
  select: 'select',
  switch: 'toggle',
  chips: 'chips',
  // A caller-composed control describes ITSELF; the row falls back to the field
  // shape it wraps rather than guessing a kind it cannot see.
  custom: 'field',
};

/** Plain-text readback of a control's current value, for the locked treatment. */
/**
 * Visual rows a textarea needs, approximating wrap. Counting NEWLINES is not
 * counting LINES: a one-paragraph description with no newline in it wraps to
 * three or four visual rows, and the previous newline-count sizing clipped the
 * rest. Invisible in jsdom (scrollHeight is always 0 there, so a measured
 * implementation could not be covered by a test) and plainly visible the first
 * time the composer was rendered.
 *
 * CHARS_PER_ROW approximates the control's width in the composer's field
 * column. Deliberately an estimate: the stylesheet lets the control scroll if
 * it falls short, so nothing is ever silently hidden.
 */
const CHARS_PER_ROW = 46;
function estimateTextareaRows(value: string): number {
  const wrapped = value
    .split(String.fromCharCode(10))
    .reduce((rows, line) => rows + Math.max(1, Math.ceil(line.length / CHARS_PER_ROW)), 0);
  return Math.min(10, Math.max(2, wrapped));
}
function lockedControlValue(control: TypeDefinitionRowControl, t: (key: string) => string, emptyPlaceholder: string): string {
  // A control's OWN placeholder wins over the generic "Not set", exactly as it
  // does in edit mode (`control.placeholder ?? emptyPlaceholder`). Locked mode
  // used to discard it, so a row whose empty state carries real meaning lost it:
  // `Extends` is always locked, so a root type read "Not set" instead of "A root
  // type — no parent" — the one place that row has something to say.
  const empty = ('placeholder' in control && control.placeholder) || emptyPlaceholder;
  switch (control.kind) {
    case 'text':
    case 'multiline':
    case 'select':
      return control.value || empty;
    case 'number':
      return control.value === null ? empty : String(control.value);
    case 'switch':
      return control.checked ? t('studio.typeEditor.advanced.yes') : t('studio.typeEditor.advanced.no');
    case 'chips':
      return control.values.length > 0 ? control.values.join(', ') : empty;
    default:
      return empty;
  }
}

/**
 * Escape reverts a text-like field to its last-saved value (spec Keyboard map:
 * "Field row … Escape reverts the field to its last saved value"). The control is
 * fully parent-controlled — this row never owns the value — so "last saved" is
 * captured as whatever value was present when this focus session began: nothing
 * external changes it between a commit (blur) and the next focus, so value-at-focus
 * equals value-at-last-commit. Reverting only calls `onChange`, never `onCommit`
 * — the reverted value already matches server state, so no save is needed.
 */
function useEscapeRevert(currentValue: string, onChange: ((value: string) => void) | undefined) {
  const committedRef = React.useRef(currentValue);
  const handleFocus = () => {
    committedRef.current = currentValue;
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onChange?.(committedRef.current);
    }
  };
  return { handleFocus, handleKeyDown };
}

type NumberControlSpec = Extract<TypeDefinitionRowControlSpec, { kind: 'number' }>;

/** Parses a number field's draft: `null` for an emptied field, `undefined` when the draft is
 * not a finite number within `min`/`max` (or not whole when `step` is) — the caller reverts. */
function parseNumberDraft(draft: string, control: NumberControlSpec): number | null | undefined {
  if (draft.trim() === '') return null;
  const parsed = Number(draft);
  if (!Number.isFinite(parsed)) return undefined;
  if (control.min !== undefined && parsed < control.min) return undefined;
  if (control.max !== undefined && parsed > control.max) return undefined;
  if (control.step !== undefined && Number.isInteger(control.step) && !Number.isInteger(parsed)) return undefined;
  return parsed;
}

/**
 * The `number` control: a local draft while focused, committed on blur or Enter (see the
 * `TypeDefinitionRowControlSpec` note). Escape drops the draft without committing — the same
 * contract as the text controls' `useEscapeRevert`.
 */
function NumberControl({
  control,
  shared,
  emptyPlaceholder,
  editable,
}: {
  control: NumberControlSpec & { onCommit?: () => void };
  shared: Record<string, unknown>;
  emptyPlaceholder: string;
  editable: boolean;
}): React.ReactElement {
  const committed = control.value === null ? '' : String(control.value);
  const [draft, setDraft] = React.useState<string | null>(null);
  const inputMode = control.step !== undefined && !Number.isInteger(control.step) ? 'decimal' : 'numeric';
  const commit = () => {
    if (draft !== null) {
      const parsed = parseNumberDraft(draft, control);
      if (parsed !== undefined && parsed !== control.value) control.onChange?.(parsed);
      setDraft(null);
    }
    control.onCommit?.();
  };
  return (
    <InputAtom
      {...shared}
      className="type-definition-row__control-input"
      type="number"
      inputMode={inputMode}
      min={control.min}
      max={control.max}
      step={control.step}
      value={draft ?? committed}
      placeholder={control.placeholder ?? emptyPlaceholder}
      disabled={!editable}
      readOnly={!editable}
      onChange={editable ? (e) => setDraft(e.target.value) : undefined}
      onKeyDown={
        editable
          ? (e) => {
              if (e.key === 'Escape') setDraft(null);
              if (e.key === 'Enter') commit();
            }
          : undefined
      }
      onBlur={editable ? commit : undefined}
    />
  );
}

/**
 * A JSX component (not a plain function) — `useEscapeRevert` calls a hook, and
 * this is rendered once per control in a `.map()`; each gets its own fiber via
 * its `key`, so the hook is safe here in a way it would not be as a bare
 * function called inline in another component's render body.
 *
 * `useEscapeRevert` is called ONCE, above the switch, and never inside a branch:
 * `control.kind` changes on the same fiber (a row flipping `custom`→`text` when it
 * unlocks, or `text`→`select` when the property's type changes), so a per-branch
 * call would change the hook count between renders and throw "Rendered fewer hooks
 * than expected". Only the text and multiline branches consume it.
 */
function FieldControl({
  control,
  controlId,
  emptyPlaceholder,
  mode,
  invalid,
  describedBy,
}: {
  control: TypeDefinitionRowControl;
  controlId: string;
  emptyPlaceholder: string;
  mode: TypeFieldRowMode;
  invalid?: boolean;
  describedBy?: string;
}): React.ReactElement {
  const editable = mode === 'edit' || mode === 'ghost';
  const escapeRevert = useEscapeRevert(
    control.kind === 'text' || control.kind === 'multiline' ? control.value : '',
    control.kind === 'text' || control.kind === 'multiline' ? control.onChange : undefined
  );
  const shared = {
    id: controlId,
    'aria-label': control.ariaLabel,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
    'data-testid': 'type-definition-row-control',
    'data-datum': control.datum,
    'data-kind': control.datumKind,
  };

  switch (control.kind) {
    case 'custom':
      return <>{control.node}</>;
    case 'text': {
      const { handleFocus, handleKeyDown } = escapeRevert;
      return (
        <InputAtom
          {...shared}
          className="type-definition-row__control-input"
          value={control.value}
          placeholder={control.placeholder ?? emptyPlaceholder}
          disabled={!editable}
          readOnly={!editable}
          onChange={editable ? (e) => control.onChange?.(e.target.value) : undefined}
          onFocus={editable ? handleFocus : undefined}
          onKeyDown={editable ? handleKeyDown : undefined}
          onBlur={editable ? () => control.onCommit?.() : undefined}
        />
      );
    }
    case 'number':
      return <NumberControl control={control} shared={shared} emptyPlaceholder={emptyPlaceholder} editable={editable} />;
    case 'multiline': {
      const { handleFocus, handleKeyDown } = escapeRevert;
      return (
        <TextareaAtom
          {...shared}
          className="type-definition-row__control-textarea"
          value={control.value}
          placeholder={control.placeholder ?? emptyPlaceholder}
          // Grows with content (spec `descriptionControlGrowsToItsContent`) rather than
          // a fixed 2 rows that clips a longer description. A rows-count strategy is
          // used instead of PrimeReact's `autoResize` because that measures real
          // `scrollHeight`, which jsdom always reports as 0 — untestable and, worse,
          // would size the control to nothing in a test environment.
          rows={estimateTextareaRows(control.value)}
          resize="none"
          disabled={!editable}
          readOnly={!editable}
          onChange={editable ? (e) => control.onChange?.(e.target.value) : undefined}
          onFocus={editable ? handleFocus : undefined}
          onKeyDown={editable ? handleKeyDown : undefined}
          onBlur={editable ? () => control.onCommit?.() : undefined}
        />
      );
    }
    case 'select':
      return (
        <DropdownAtom
          {...shared}
          className="type-definition-row__control-select"
          dropdownSize="small"
          value={control.value}
          // Phase 2 supplies the real option set. Until then the current value is
          // the only option, which is truthful for a disabled control: it shows the
          // shape (a dropdown) without inventing choices the API has not described.
          options={control.options ?? [{ label: control.value, value: control.value }]}
          optionLabel="label"
          optionValue="value"
          placeholder={emptyPlaceholder}
          disabled={!editable}
          onChange={
            editable
              ? (e) => {
                  control.onChange?.(e.value);
                  control.onCommit?.();
                }
              : undefined
          }
        />
      );
    case 'switch':
      return (
        <SwitchAtom
          inputId={controlId}
          ariaLabel={control.ariaLabel}
          className="type-definition-row__control-switch"
          checked={control.checked}
          disabled={!editable}
          onChange={
            editable
              ? (checked) => {
                  control.onChange?.(checked);
                  control.onCommit?.();
                }
              : undefined
          }
        />
      );
    case 'chips':
      return (
        <ChipsAtom
          {...shared}
          className="type-definition-row__control-chips"
          value={control.values}
          onChange={
            editable
              ? (values) => {
                  control.onChange?.(values);
                  control.onCommit?.();
                }
              : // Required by ChipsAtom's controlled contract. Unreachable while
                // `disabled` — a no-op rather than wired to anything.
                () => undefined
          }
          placeholder={control.values.length === 0 ? control.placeholder ?? emptyPlaceholder : undefined}
          disabled={!editable}
        />
      );
    default:
      return <></>;
  }
}

const TypeFieldRowMolecule: React.FC<TypeFieldRowMoleculeProps> = ({
  label,
  leading,
  lockReason,
  secondary,
  afterLabel,
  meta,
  controls,
  badges,
  provenance,
  trailing,
  className = '',
  'data-testid': dataTestId,
  'data-container': dataContainer,
  mode = 'disabled',
  ghostRationale,
  onKeepGhost,
  onDiscardGhost,
  onGhostEscape,
  datum,
  kind,
  layout = 'inline',
  hintPlacement = 'below',
  wrap = 'wrap',
  labelDatum,
  secondaryDatum,
  hint,
  hintDatum,
  errorMessage,
  errorDatum,
}) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const controlId = (index: number) => `type-definition-row-control-${generatedId}-${index}`;
  const errorId = errorMessage ? `type-definition-row-error-${generatedId}` : undefined;
  const hasControls = !!controls && controls.length > 0;
  const locked = mode === 'locked';
  const isGhost = mode === 'ghost';
  const resolvedKind = kind ?? (controls?.[0] ? CONTROL_KIND_TO_DATUM_KIND[controls[0].kind] : 'static');
  const rootClasses = [
    'type-definition-row',
    hasControls && 'type-definition-row--with-controls',
    hasControls && locked && 'type-definition-row--locked',
    isGhost && 'type-definition-row--ghost',
    layout === 'stacked' && 'type-definition-row--stacked',
    hintPlacement === 'inline' && 'type-definition-row--hint-inline',
    wrap === 'nowrap' && 'type-definition-row--nowrap',
    !!errorMessage && 'type-definition-row--invalid',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const emptyPlaceholder = t('studio.typeEditor.row.notSet');

  const handleRootKeyDown = isGhost
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') onGhostEscape?.();
      }
    : undefined;

  return (
    <div
      className={rootClasses}
      data-testid={dataTestId}
      // Self-description for the parity walker (nx run design-system:parity-capture):
      // what this row IS, in the prototype's own vocabulary. `data-kind` is the
      // first control's kind (or `static` for a readback row); `data-state`
      // names the rendering mode and lock reason. The walker diffs these
      // against the decomposition's tree for the story's declared state.
      data-ui="field"
      data-datum={datum}
      data-container={dataContainer}
      data-kind={resolvedKind}
      data-layout={layout}
      data-state={[mode, lockReason ? `lock:${lockReason}` : null].filter(Boolean).join(' ')}
      onKeyDown={handleRootKeyDown}
    >
      <div className="type-definition-row__main">
        {leading && <span className="type-definition-row__leading">{leading}</span>}
        {label !== undefined &&
          (hasControls && !locked ? (
            // Tied to the first control so the field is announced by its visible
            // name; later controls in the same row carry their own aria-label.
            <label className="type-definition-row__label" htmlFor={controlId(0)} data-datum={labelDatum} data-kind={labelDatum ? 'label' : undefined}>
              {label}
            </label>
          ) : (
            <span className="type-definition-row__label" data-datum={labelDatum} data-kind={labelDatum ? 'label' : undefined}>
              {label}
            </span>
          ))}
        {afterLabel}
        {secondary && (
          <span className="type-definition-row__secondary" data-datum={secondaryDatum} data-kind={secondaryDatum ? 'note' : undefined}>
            {secondary}
          </span>
        )}
        {meta}
        {hint && hintPlacement === 'inline' && (
          <span className="type-definition-row__hint type-definition-row__hint--inline" data-datum={hintDatum} data-kind={hintDatum ? 'hint' : undefined}>
            {hint}
          </span>
        )}
        {hasControls && locked && (
          <div className="type-definition-row__locked-values" data-testid="type-definition-row-locked">
            {controls.map((control) => (
              <span key={control.ariaLabel} className="type-definition-row__locked-value">
                {lockedControlValue(control, t, emptyPlaceholder)}
              </span>
            ))}
            <TagAtom
              tone="outline"
              size="small"
              icon="pi-lock"
              label={t(`studio.typeEditor.composer.lockReason.${lockReason ?? 'platform'}`)}
              data-testid="type-definition-row-lock-chip"
            />
          </div>
        )}
        {hasControls && !locked && (
          <div className="type-definition-row__controls">
            {controls.map((control, index) => (
              <div
                key={control.ariaLabel}
                className={[
                  'type-definition-row__control',
                  control.grow && 'type-definition-row__control--grow',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <FieldControl
                  control={control}
                  controlId={controlId(index)}
                  emptyPlaceholder={emptyPlaceholder}
                  mode={mode}
                  invalid={index === 0 ? !!errorMessage : undefined}
                  describedBy={index === 0 ? errorId : undefined}
                />
              </div>
            ))}
          </div>
        )}
        {badges?.map((badge) => (
          <TagAtom key={badge.label} label={badge.label} tone={badge.tone ?? 'outline'} size="small" />
        ))}
        {hint && hintPlacement === 'below' && (
          <p className="type-definition-row__hint" data-datum={hintDatum} data-kind={hintDatum ? 'hint' : undefined}>
            {hint}
          </p>
        )}
        {errorMessage && (
          <p
            id={errorId}
            className="type-definition-row__error"
            role="alert"
            data-datum={errorDatum}
            data-kind={errorDatum ? 'note' : undefined}
          >
            {errorMessage}
          </p>
        )}
      </div>
      {provenance && (
        <div className="type-definition-row__provenance" data-testid="type-definition-row-provenance">
          {provenance.isLegacy ? (
            <TagAtom
              tone="outline"
              size="small"
              icon="pi-history"
              label={t('studio.typeEditor.row.legacy')}
              data-datum={provenance.datum}
              data-kind={provenance.datum ? 'note' : undefined}
            />
          ) : provenance.isInherited ? (
            <TagAtom
              tone="info"
              size="small"
              icon="pi-arrow-up-right"
              label={t('studio.typeEditor.row.inheritedFrom', {
                schema: provenance.inheritedFromSchema ?? t('studio.typeEditor.row.unknownAncestor'),
              })}
              data-datum={provenance.datum}
              data-kind={provenance.datum ? 'note' : undefined}
            />
          ) : (
            <TagAtom tone="outline" size="small" label={t('studio.typeEditor.row.own')} />
          )}
        </div>
      )}
      {isGhost && (
        <div className="type-definition-row__ghost" data-testid="type-definition-row-ghost">
          <TagAtom
            tone="accent"
            size="small"
            icon="pi-sparkles"
            label={t('studio.typeEditor.composer.ghost.proposed')}
            data-testid="type-definition-row-ghost-mark"
          />
          {ghostRationale && (
            <span className="type-definition-row__ghost-rationale" data-testid="type-definition-row-ghost-rationale">
              {ghostRationale}
            </span>
          )}
          {/*
            Omitted, not disabled, when neither handler is given: the composer answers a proposal
            once in its section's ghost bar, so these rows carry no pair at all there. A disabled
            control that cannot state why it is disabled is a defect in its own right
            (`/standards/frontend/ux-craft.md` I2).
          */}
          {(onKeepGhost || onDiscardGhost) && (
            <div className="type-definition-row__ghost-actions">
              <ButtonAtom
                variant="text"
                size="small"
                onClick={onDiscardGhost}
                disabled={!onDiscardGhost}
                data-testid="type-definition-row-ghost-discard"
              >
                {t('studio.typeEditor.composer.ghost.discard')}
              </ButtonAtom>
              <ButtonAtom
                variant="secondary"
                size="small"
                onClick={onKeepGhost}
                disabled={!onKeepGhost}
                data-testid="type-definition-row-ghost-keep"
              >
                {t('studio.typeEditor.composer.ghost.keep')}
              </ButtonAtom>
            </div>
          )}
        </div>
      )}
      {!isGhost && trailing && <div className="type-definition-row__trailing">{trailing}</div>}
    </div>
  );
};

TypeFieldRowMolecule.displayName = 'TypeFieldRowMolecule';

export default TypeFieldRowMolecule;
